import { Chip, Collapse, Icon, IconButton, Paper, Typography } from '@shared/ui';
import { useState } from 'react';
import { registerWidgetRenderer } from '../registry';
import type { DebateResponseEntity, TimelineWidgetProps } from '../types';

export interface DebateResponseWidgetProps extends TimelineWidgetProps<DebateResponseEntity> {
  debugMode?: boolean;
}

registerWidgetRenderer(
  'debate_response',
  ({ entity, debugMode }) => (
    <DebateResponseWidget key={(entity as any).id} entity={entity as any} debugMode={debugMode} />
  ),
  { visibility: { normal: true, debug: true } }
);

export function DebateResponseWidget({ entity, debugMode = false }: DebateResponseWidgetProps) {
  const { status, persona_id, round_number, response, tone } = entity.props;
  const [expanded, setExpanded] = useState(true);

  const statusColor = status === 'completed' ? 'success' : status === 'error' ? 'error' : 'primary';
  const toneColor = tone === 'forceful' ? 'error' : tone === 'moderate' ? 'warning' : 'info';

  return (
    <Paper
      variant="outlined"
      className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 mp-block artifact-content"
    >
      <div className="flex items-start gap-3">
        <Icon sx={{ fontSize: 24, color: `${statusColor}.main` }}>
          {status === 'completed' ? 'record_voice_over' : 'mic'}
        </Icon>

        <div className="flex-1">
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              💬 Response
            </Typography>
            {persona_id ? (
              <Chip label={persona_id} size="small" color="primary" variant="outlined" />
            ) : null}
            {round_number !== undefined ? (
              <Chip label={`Round ${round_number}`} size="small" variant="outlined" />
            ) : null}
            {tone ? <Chip label={tone} size="small" color={toneColor} variant="outlined" /> : null}
            <IconButton size="small" sx={{ ml: 'auto' }}>
              <Icon sx={{ fontSize: 18 }}>{expanded ? 'expand_less' : 'expand_more'}</Icon>
            </IconButton>
          </div>

          <Collapse in={expanded}>
            {status === 'active' || status === 'completed' ? (
              <div className="mt-3 space-y-3">
                {response ? (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="whitespace-pre-wrap"
                    >
                      {response}
                    </Typography>
                  </div>
                ) : null}

                {status === 'completed' && entity.props.error ? (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <Typography variant="body2" color="error">
                      Error: {entity.props.error}
                    </Typography>
                  </div>
                ) : null}

                {debugMode ? (
                  <details className="bg-gray-100 rounded p-2">
                    <summary className="cursor-pointer text-xs font-mono">Debug Info</summary>
                    <pre className="text-xs mt-2 overflow-auto max-h-48">
                      {JSON.stringify(entity.props, null, 2)}
                    </pre>
                  </details>
                ) : null}
              </div>
            ) : null}

            {status === 'error' ? (
              <div className="mt-3">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <Typography variant="body2" color="error">
                    {entity.props.error || 'An error occurred while processing response'}
                  </Typography>
                </div>
              </div>
            ) : null}
          </Collapse>
        </div>
      </div>
    </Paper>
  );
}
